import type { Plugin } from 'chart.js';

const FONT = '600 13px Inter, sans-serif';
const HORIZONTAL_LENGTH = 28;
const BEND_OFFSET = 16;
const TEXT_PADDING = 4;
const LABEL_HEIGHT = 16;
const BOX_GAP = 3;
const MIN_SLICE_PERCENT = 0;

type PieArcPoint = {
  startAngle: number;
  endAngle: number;
  x: number;
  y: number;
  outerRadius: number;
};

type TextBox = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

type LabelLayout = {
  percentage: string;
  slicePercent: number;
  defaultRightSide: boolean;
  startX: number;
  startY: number;
  bendX: number;
  bendY: number;
  endX: number;
  endY: number;
  rightSide: boolean;
  textWidth: number;
};

function endXForSide(bendX: number, rightSide: boolean) {
  return bendX + (rightSide ? HORIZONTAL_LENGTH : -HORIZONTAL_LENGTH);
}

/** Mid-angle of the slice arc (center of the wedge). */
function getArcMidAngle(arc: PieArcPoint): number {
  return (arc.startAngle + arc.endAngle) / 2;
}

/** Point on the outer edge of the slice at the given angle. */
function getOuterArcPoint(arc: PieArcPoint, angle: number) {
  return {
    x: arc.x + Math.cos(angle) * arc.outerRadius,
    y: arc.y + Math.sin(angle) * arc.outerRadius,
  };
}

/** Elbow point just outside the slice along the mid-angle. */
function getOutwardPoint(arc: PieArcPoint, angle: number) {
  const radius = arc.outerRadius + BEND_OFFSET;
  return {
    x: arc.x + Math.cos(angle) * radius,
    y: arc.y + Math.sin(angle) * radius,
  };
}

function buildRadialLabel(
  arc: PieArcPoint,
  percentage: string,
  slicePercent: number,
  angle: number,
  ctx: CanvasRenderingContext2D
): LabelLayout {
  const outer = getOuterArcPoint(arc, angle);
  const outward = getOutwardPoint(arc, angle);
  const rightSide = Math.cos(angle) >= 0 && Math.sin(angle) <= 0.35;

  return {
    percentage,
    slicePercent,
    defaultRightSide: rightSide,
    startX: outer.x,
    startY: outer.y,
    bendX: outward.x,
    bendY: outward.y,
    endX: endXForSide(outward.x, rightSide),
    endY: outward.y,
    rightSide,
    textWidth: ctx.measureText(percentage).width,
  };
}

function getTextBox(label: LabelLayout): TextBox {
  const anchorX = label.endX + (label.rightSide ? TEXT_PADDING : -TEXT_PADDING);
  const left = label.rightSide ? anchorX : anchorX - label.textWidth;
  const right = label.rightSide ? anchorX + label.textWidth : anchorX;

  return {
    left,
    right,
    top: label.endY - LABEL_HEIGHT / 2,
    bottom: label.endY + LABEL_HEIGHT / 2,
  };
}

function boxesOverlap(a: TextBox, b: TextBox) {
  return !(a.right + BOX_GAP < b.left || a.left > b.right + BOX_GAP || a.bottom + BOX_GAP < b.top || a.top > b.bottom + BOX_GAP);
}

function hasCollision(label: LabelLayout, placed: LabelLayout[]) {
  const box = getTextBox(label);
  return placed.some((other) => boxesOverlap(box, getTextBox(other)));
}

function applyCandidate(label: LabelLayout, rightSide: boolean, endY: number) {
  label.rightSide = rightSide;
  label.endY = endY;
  label.endX = endXForSide(label.bendX, rightSide);
}

function placeLabels(labels: LabelLayout[]) {
  const placed: LabelLayout[] = [];
  const sorted = [...labels].sort((a, b) => b.slicePercent - a.slicePercent);

  for (const label of sorted) {
    const yOffsets = [0, 18, -18, 36, -36, 54, -54];
    const sides = label.defaultRightSide === false ? [false, true] : [label.defaultRightSide, !label.defaultRightSide];

    let positioned = false;

    for (const rightSide of sides) {
      for (const yOffset of yOffsets) {
        applyCandidate(label, rightSide, label.bendY + yOffset);

        if (!hasCollision(label, placed)) {
          placed.push(label);
          positioned = true;
          break;
        }
      }
      if (positioned) break;
    }

    if (!positioned) {
      applyCandidate(label, label.defaultRightSide, label.bendY);
      placed.push(label);
    }
  }

  return placed;
}

export const PieChartLabelsPlugin: Plugin<'pie'> = {
  id: 'pieLabelPlugin',

  afterDatasetsDraw(chart) {
    const pluginOptions = (
      chart.options.plugins as {
        pieLabelPlugin?: { display?: boolean; preferLeftSide?: boolean; minSlicePercent?: number };
      }
    )?.pieLabelPlugin;

    if (!pluginOptions?.display) return;

    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);

    if (!dataset || !meta) return;

    const data = dataset.data as number[];
    const total = data.reduce((sum, value) => sum + Number(value), 0);

    if (!total) return;

    const preferLeftSide = pluginOptions.preferLeftSide ?? false;
    const minSlicePercent = pluginOptions.minSlicePercent ?? MIN_SLICE_PERCENT;

    ctx.save();
    ctx.font = FONT;
    ctx.fillStyle = '#444';
    ctx.strokeStyle = '#BDBDBD';
    ctx.lineWidth = 1;
    ctx.textBaseline = 'middle';

    const labels: LabelLayout[] = [];

    for (let index = 0; index < meta.data.length; index++) {
      const arc = meta.data[index] as unknown as PieArcPoint;
      const value = Number(data[index]);
      if (!value) continue;

      const slicePercent = (value / total) * 100;
      if (slicePercent < minSlicePercent) continue;

      const percentage = `${slicePercent.toFixed(1)}%`;
      const angle = getArcMidAngle(arc);

      if (preferLeftSide && Math.cos(angle) >= 0) continue;

      labels.push(buildRadialLabel(arc, percentage, slicePercent, angle, ctx));
    }

    const placedLabels = placeLabels(labels);

    placedLabels.forEach((label) => {
      ctx.beginPath();
      ctx.moveTo(label.startX, label.startY);
      ctx.lineTo(label.bendX, label.bendY);
      ctx.lineTo(label.endX, label.endY);
      ctx.stroke();

      ctx.textAlign = label.rightSide ? 'left' : 'right';
      ctx.fillText(label.percentage, label.endX + (label.rightSide ? TEXT_PADDING : -TEXT_PADDING), label.endY);
    });

    ctx.restore();
  },
};

export default PieChartLabelsPlugin;
